import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';

const AuthContext = createContext({})

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [userProfile, setUserProfile] = useState(null)
  const [loading, setLoading] = useState(true)
  const [profileLoading, setProfileLoading] = useState(false)
  const [authError, setAuthError] = useState(null)

  // Separate async operations object
  const profileOperations = {
    async load(userId) {
      if (!userId) return
      setProfileLoading(true)
      try {
        const { data, error } = await supabase
          ?.from('user_profiles')
          ?.select('*')
          ?.eq('id', userId)
          ?.single()
        
        if (!error && data) {
          setUserProfile(data)
        } else if (error) {
          setAuthError(`Failed to load user profile: ${error?.message}`)
        }
      } catch (error) {
        if (error?.message?.includes('Failed to fetch') || 
            error?.message?.includes('NetworkError')) {
          setAuthError('Cannot connect to database. Your Supabase project may be paused or inactive. Please check your Supabase dashboard.')
        } else {
          setAuthError('Failed to load user profile')
        }
      } finally {
        setProfileLoading(false)
      }
    },
    
    clear() {
      setUserProfile(null)
      setProfileLoading(false)
    }
  }

  // Protected auth handlers
  const authStateHandlers = {
    // CRITICAL: This MUST remain synchronous
    onChange: (event, session) => {
      setUser(session?.user ?? null)
      setLoading(false)
      
      if (session?.user) {
        profileOperations?.load(session?.user?.id) // Fire-and-forget
      } else {
        profileOperations?.clear()
      }
    }
  }

  useEffect(() => {
    // Get initial session
    supabase?.auth?.getSession()?.then(({ data: { session } }) => {
      authStateHandlers?.onChange(null, session)
    })

    // Listen for auth changes - PROTECTED: Never modify this callback
    const { data: { subscription } } = supabase?.auth?.onAuthStateChange(
      authStateHandlers?.onChange
    )

    return () => subscription?.unsubscribe()
  }, [])

  // Enhanced session validation and refresh
  const refreshSession = async () => {
    try {
      setAuthError(null)
      const { data, error } = await supabase?.auth?.refreshSession()
      
      if (error) {
        setAuthError(`Session refresh failed: ${error?.message}. Please sign in again.`)
        return { success: false, error: error?.message }
      }

      if (data?.session) {
        setUser(data?.session?.user)
        if (data?.session?.user?.id) {
          await profileOperations?.load(data?.session?.user?.id)
        }
        return { success: true, session: data?.session }
      }

      return { success: false, error: 'No session returned after refresh' }
    } catch (error) {
      const errorMsg = 'Failed to refresh session. Please sign in again.'
      setAuthError(errorMsg)
      return { success: false, error: errorMsg }
    }
  }

  // Check and refresh session if needed
  const ensureValidSession = async () => {
    try {
      const { data: { session }, error } = await supabase?.auth?.getSession()
      
      if (error) {
        throw new Error(`Session check failed: ${error?.message}`)
      }
      
      if (!session) {
        throw new Error('No active session found')
      }

      // Check if session expires soon (within 10 minutes)
      const expiresAt = new Date(session?.expires_at * 1000)
      const now = new Date()
      const timeUntilExpiry = expiresAt?.getTime() - now?.getTime()
      const tenMinutes = 10 * 60 * 1000

      if (timeUntilExpiry <= tenMinutes) {
        console.log('Session expires soon, attempting refresh...')
        return await refreshSession()
      }

      return { success: true, session }
    } catch (error) {
      setAuthError(error?.message)
      return { success: false, error: error?.message }
    }
  }

  // Auth methods
  const signIn = async (email, password) => {
    try {
      setAuthError(null)
      const { data, error } = await supabase?.auth?.signInWithPassword({
        email,
        password
      })
      
      if (error) {
        setAuthError(error?.message)
        return { success: false, error: error?.message };
      }

      return { success: true, user: data?.user };
    } catch (error) {
      if (error?.message?.includes('Failed to fetch') || 
          error?.message?.includes('AuthRetryableFetchError')) {
        const errorMsg = 'Cannot connect to authentication service. Your Supabase project may be paused or inactive. Please check your Supabase dashboard and resume your project if needed.'
        setAuthError(errorMsg)
        return { success: false, error: errorMsg }
      }
      
      const errorMsg = 'Something went wrong during sign in. Please try again.'
      setAuthError(errorMsg)
      return { success: false, error: errorMsg }
    }
  }

  const signUp = async (email, password, metadata = {}) => {
    try {
      setAuthError(null)
      const { data, error } = await supabase?.auth?.signUp({
        email,
        password,
        options: {
          data: metadata
        }
      })
      
      if (error) {
        setAuthError(error?.message)
        return { success: false, error: error?.message };
      }

      return { success: true, user: data?.user };
    } catch (error) {
      if (error?.message?.includes('Failed to fetch') || 
          error?.message?.includes('AuthRetryableFetchError')) {
        const errorMsg = 'Cannot connect to authentication service. Your Supabase project may be paused or inactive. Please check your Supabase dashboard and resume your project if needed.'
        setAuthError(errorMsg)
        return { success: false, error: errorMsg }
      }
      
      const errorMsg = 'Something went wrong during sign up. Please try again.'
      setAuthError(errorMsg)
      return { success: false, error: errorMsg }
    }
  }

  const signOut = async () => {
    try {
      setAuthError(null)
      const { error } = await supabase?.auth?.signOut()
      
      if (error) {
        setAuthError(error?.message)
        return { success: false, error: error?.message };
      }

      return { success: true }
    } catch (error) {
      if (error?.message?.includes('Failed to fetch') || 
          error?.message?.includes('NetworkError')) {
        const errorMsg = 'Cannot connect to authentication service. You may have been signed out locally.'
        setAuthError(errorMsg)
        // Still return success as user is effectively signed out locally
        return { success: true }
      }
      
      const errorMsg = 'Something went wrong during sign out. Please try again.'
      setAuthError(errorMsg)
      return { success: false, error: errorMsg }
    }
  }

  const updateProfile = async (updates) => {
    try {
      setAuthError(null)
      if (!user?.id) {
        throw new Error('No authenticated user')
      }

      const { data, error } = await supabase?.from('user_profiles')?.update({
          ...updates,
          updated_at: new Date()?.toISOString()
        })?.eq('id', user?.id)?.select()?.single()

      if (error) {
        setAuthError(`Failed to update profile: ${error?.message}`)
        return { success: false, error: error?.message };
      }

      setUserProfile(data)
      return { success: true, profile: data }
    } catch (error) {
      if (error?.message?.includes('Failed to fetch') || 
          error?.message?.includes('NetworkError')) {
        const errorMsg = 'Cannot connect to database. Your Supabase project may be paused or inactive.'
        setAuthError(errorMsg)
        return { success: false, error: errorMsg }
      }
      
      const errorMsg = 'Failed to update profile'
      setAuthError(errorMsg)
      return { success: false, error: errorMsg }
    }
  }

  const clearError = () => {
    setAuthError(null)
  }

  const value = {
    user,
    userProfile,
    loading,
    profileLoading,
    authError,
    signIn,
    signUp,
    signOut,
    updateProfile,
    clearError,
    refreshSession,
    ensureValidSession
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export default AuthProvider